import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { BanksService } from "../../../services/banks.service";
import { ParticipantService } from '../../../services/participant.service';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ReportHubService } from '../../../services/report-hub.service';
import { ToastService } from '../../../services/toast.service';
import * as XLSX from 'xlsx';

interface DropdownOption {
  label: string;
  value: string | number;
}

interface ReportType {
  value: string;
  label: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-banks-reports',
  templateUrl: './banks-reports.component.html',
  styleUrls: ['./banks-reports.component.scss']
})
export class BanksReportsComponent implements OnInit, OnDestroy {
  reportForm!: FormGroup;
  loading = false;
  step = 1;

  // Preview variables
  showPreview = false;
  previewData: any[][] = [];
  previewLoading = false;

  private reportHubSub!: Subscription;
  private pendingJobId: string | null = null;
  private pendingAction: 'download' | 'preview' | null = null;

  reportTypes: ReportType[] = [
    {
      value: 'movements',
      label: 'Reporte de Movimientos',
      description: 'Muestra ingresos, egresos, saldo inicial y final para un período.',
      icon: '📊'
    },
    {
      value: 'balance',
      label: 'Reporte de Saldos de Bancos',
      description: 'Muestra únicamente el saldo final consolidado a una fecha de corte.',
      icon: '💰'
    }
  ];

  months = [
    { value: 1, name: 'Enero' },
    { value: 2, name: 'Febrero' },
    { value: 3, name: 'Marzo' },
    { value: 4, name: 'Abril' },
    { value: 5, name: 'Mayo' },
    { value: 6, name: 'Junio' },
    { value: 7, name: 'Julio' },
    { value: 8, name: 'Agosto' },
    { value: 9, name: 'Septiembre' },
    { value: 10, name: 'Octubre' },
    { value: 11, name: 'Noviembre' },
    { value: 12, name: 'Diciembre' }
  ];

  years: number[] = [];
  banks: DropdownOption[] = [];

  // Date Type Filter
  dateType: string = 'Creation';
  dateTypeOptions: any[] = [
    { label: 'Fecha de Creación', value: 'Creation' },
    { label: 'Fecha de Pago', value: 'Payment' }
  ];

  // Dropdown options for PrimeNG
  monthsDropdown: DropdownOption[] = [];
  yearsDropdown: DropdownOption[] = [];
  banksDropdown: DropdownOption[] = [];
  participantsDropdown: DropdownOption[] = [];
  private participants: any[] = [];

  constructor(
    private fb: FormBuilder,
    private _banksService: BanksService,
    private _reportJobsService: ReportJobsService,
    private _reportHubService: ReportHubService,
    private _toastService: ToastService,
    private _participantsService: ParticipantService,
  ) {
    // Generar años disponibles
    const currentYear = new Date().getFullYear();
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      this.years.push(year);
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.initializeDropdowns();
    this.loadBanks();
    this.loadParticipants();
    this.setupReportTypeListener();

    this.reportHubSub = this._reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        if (this.pendingAction === 'preview') {
          this._reportJobsService.fetchReportBlob(notification.jobId).subscribe({
            next: (blob) => {
              this.loadExcelPreview(blob);
              this._toastService.showToast('success', 'Previsualización lista');
            },
            error: () => {
              this.previewLoading = false;
              this._toastService.showToast('error', 'Error al obtener previsualización');
            }
          });
        } else {
          this.loading = false;
          this._toastService.showToast('success', 'Reporte bancario generado exitosamente. Descargando...');
          this._reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        }
        this.pendingJobId = null;
        this.pendingAction = null;
      } else if (notification.status === 'Failed') {
        this.loading = false;
        this.previewLoading = false;
        this._toastService.showToast('error', `Error al generar el reporte: ${notification.errorMessage || 'Desconocido'}`);
        this.pendingJobId = null;
        this.pendingAction = null;
      } else if (notification.status === 'Processing') {
        this._toastService.showToast('info', 'Procesando reporte bancario...');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
  }

  private initializeDropdowns(): void {
    // Initialize months dropdown
    this.monthsDropdown = this.months.map(month => ({
      label: month.name,
      value: month.value
    }));

    // Initialize years dropdown
    this.yearsDropdown = this.years.map(year => ({
      label: year.toString(),
      value: year
    }));
  }

  private initForm(): void {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    
    this.reportForm = this.fb.group({
      reportType: [null, Validators.required],
      months: [[currentMonth], Validators.required], // Cambiado a array para selección múltiple
      year: [currentYear, Validators.required],
      bank: [null, Validators.required],
      dateType: [this.dateType, Validators.required],
      initialBalanceYear: [2026], // Año inicial por defecto 2026
      participantId: [null], // 🆕 Filtro opcional por participante (depuración)
    });
  }

  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  private mapParticipantOption(p: any): DropdownOption {
    return {
      label: `${p.firstName} ${p.lastName} - ${p.typeDocument || 'N/A'}: ${p.documentId || 'N/A'}`,
      value: p.id
    };
  }

  private loadParticipants(): void {
    this._participantsService.getAll().subscribe({
      next: (res: any) => {
        this.participants = this.extractData(res);
        this.participantsDropdown = this.participants.map(p => this.mapParticipantOption(p));
      },
      error: () => {
        this.participantsDropdown = [];
      }
    });
  }

  onSearchParticipants(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this._participantsService.getAll().subscribe((res: any) => {
        this.participants = this.extractData(res);
        this.participantsDropdown = this.participants.map(p => this.mapParticipantOption(p));
      });
      return;
    }
    this._participantsService.getAllFiltered(query).subscribe((res: any) => {
      this.participants = this.extractData(res);
      this.participantsDropdown = this.participants.map(p => this.mapParticipantOption(p));
    });
  }

  private setupReportTypeListener(): void {
    this.reportForm.get('reportType')?.valueChanges.subscribe(reportType => {
      const bankControl = this.reportForm.get('bank');
      if (reportType === 'balance') {
        bankControl?.clearValidators();
      } else {
        bankControl?.setValidators([Validators.required]);
      }
      bankControl?.updateValueAndValidity();
    });
  }

  private loadBanks(): void {
    this._banksService.getAll().subscribe({
      next: (response) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        let bankData: any[];
        if (Array.isArray(response)) {
          bankData = response;
        } else if (response?.data) {
          bankData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        } else {
          bankData = [];
        }
        
        this.banks = bankData.map((bank: { bankName: any; id: any; accountNumber: any; }) => ({
          label: `${bank.bankName} - ${bank.accountNumber || 'Sin cuenta'}`,
          value: bank.id
        }));
        
        // Initialize banks dropdown for PrimeNG
        this.banksDropdown = [...this.banks];
      },
      error: (error) => {
        console.error('Error al cargar bancos:', error);
        this.banksDropdown = [];
      }
    });
  }

  previewReport(): void {
    if (this.reportForm.invalid) {
      this.reportForm.markAllAsTouched();
      return;
    }

    this.previewLoading = true;
    this.showPreview = false;
    this.pendingAction = 'preview';
    this.enqueueCurrentReport();
  }

  private loadExcelPreview(blob: Blob): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convertir a array de arrays para mostrar en tabla
        this.previewData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        this.showPreview = true;
        this.previewLoading = false;
      } catch (error) {
        console.error('Error al procesar el archivo Excel:', error);
        this.previewLoading = false;
      }
    };
    reader.readAsArrayBuffer(blob);
  }

  downloadExcelReport(): void {
    if (this.reportForm.invalid) {
      this.reportForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.pendingAction = 'download';
    this.enqueueCurrentReport();
  }

  private enqueueCurrentReport(): void {
    const { months, year, bank, reportType, dateType, initialBalanceYear, participantId } = this.reportForm.value;

    if (reportType === 'movements') {
      this._reportJobsService.queueBankExcelReportMultiMonth({
        months, year, bankId: bank, dateType, initialBalanceYear, participantId
      }).subscribe({
        next: (response) => {
          this.pendingJobId = response.jobId;
          this._toastService.showToast('info', 'Generando reporte bancario en segundo plano...');
        },
        error: (error) => {
          console.error('Error al solicitar reporte de movimientos:', error);
          this.loading = false;
          this.previewLoading = false;
          this._toastService.showToast('error', 'Error al solicitar el reporte');
        }
      });
    } else if (reportType === 'balance') {
      const month = months[0];
      this._reportJobsService.queueBankBalanceReport({
        month, year, bankId: bank, dateType, initialBalanceYear, participantId
      }).subscribe({
        next: (response) => {
          this.pendingJobId = response.jobId;
          this._toastService.showToast('info', 'Generando reporte de saldos en segundo plano...');
        },
        error: (error) => {
          console.error('Error al solicitar reporte de saldos:', error);
          this.loading = false;
          this.previewLoading = false;
          this._toastService.showToast('error', 'Error al solicitar el reporte');
        }
      });
    }
  }

  get selectedMonths(): number[] {
    return this.reportForm.get('months')?.value || [new Date().getMonth() + 1];
  }

  get selectedYear(): number {
    return this.reportForm.get('year')?.value || new Date().getFullYear();
  }

  get selectedBankId(): number | null {
    return this.reportForm.get('bank')?.value || null;
  }

  get selectedMonthsNames(): string {
    const selectedMonths = this.selectedMonths;
    const monthNames = selectedMonths.map(monthValue => {
      const month = this.months.find(m => m.value === monthValue);
      return month ? month.name : '';
    }).filter(name => name !== '');
    return monthNames.join(', ');
  }

  get selectedBankName(): string {
    if (!this.selectedBankId) return 'Sin seleccionar';
    const bank = this.banks.find(b => b.value === this.selectedBankId);
    return bank ? bank.label : 'Sin seleccionar';
  }

  getMonthName(monthValue: number): string {
    const month = this.months.find(m => m.value === monthValue);
    return month ? month.name : '';
  }

  // --- Wizard Methods ---

  goToStep(step: number): void {
    this.step = step;
  }

  isCurrentStepValid(): boolean {
    if (this.step === 1) {
      return this.reportForm.get('reportType')?.valid ?? false;
    }
    if (this.step === 2) {
      return !!(this.reportForm.get('months')?.valid &&
             this.reportForm.get('year')?.valid &&
             this.reportForm.get('bank')?.valid &&
             this.reportForm.get('dateType')?.valid);
    }
    return false;
  }

  get selectedReportType() {
    return this.reportForm.get('reportType')?.value;
  }
  
  getSelectedReport(): ReportType | undefined {
    return this.reportTypes.find(rt => rt.value === this.selectedReportType);
  }

  get selectedDateType(): string {
    return this.reportForm.get('dateType')?.value || 'Creation';
  }

  get selectedDateTypeName(): string {
    const dateTypeOption = this.dateTypeOptions.find(option => option.value === this.selectedDateType);
    return dateTypeOption ? dateTypeOption.label : 'Fecha de Creación';
  }

  get selectedInitialBalanceYear(): number {
    return this.reportForm.get('initialBalanceYear')?.value || 2026;
  }

  get selectedParticipantId(): number | null {
    return this.reportForm.get('participantId')?.value ?? null;
  }

  get selectedParticipantName(): string {
    const id = this.selectedParticipantId;
    if (!id) return 'Todos los participantes';
    const p = this.participants.find(x => x.id === id);
    return p ? `${p.firstName} ${p.lastName} - ${p.typeDocument || 'N/A'}: ${p.documentId || 'N/A'}` : 'Todos los participantes';
  }
}
